import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Isvg from 'react-inlinesvg';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import Button from 'components/Button';
import Message from 'components/Message';
import { closeModal } from 'components/Modal';
import { ICONS } from 'constants/icons';
import { bem } from 'helpers/class-name';

import useModifiersReadOnly from '../useModifiersReadOnly';
import messages from './ModifierItemPicker.messages';
import './ReviewMenuItemsModal.scss';

const { block, element } = bem('review-menu-items-modal');

export const REVIEW_MENU_ITEMS_MODAL_NAME = 'ReviewMenuItemsModal';

const ReviewMenuItemsModal = ({ menuName, items, onSave }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { isReadOnly } = useModifiersReadOnly();
  const [keptIds, setKeptIds] = useState(() => items.map(item => item.id));

  const kept = items.filter(item => keptIds.includes(item.id));

  const handleCancel = () => dispatch(closeModal());

  const handleSave = () => {
    onSave(keptIds);
    dispatch(closeModal());
  };

  return (
    <div className={block()}>
      <header className={element('header')}>
        <div className={element('header-top')}>
          <div className={element('heading')}>
            <h2 className={element('title')}>{menuName}</h2>
            <p className={element('count')}>
              <Message message={messages.usedInItems} values={{ count: kept.length }} />
            </p>
          </div>
          <button
            type="button"
            className={element('close')}
            aria-label={intl.formatMessage(messages.reviewMenuClose)}
            onClick={handleCancel}>
            <Isvg src={ICONS.cross} className={element('close-icon')} />
          </button>
        </div>
        {!isReadOnly && (
          <Button
            styled="tertiary"
            className={element('remove-all')}
            disabled={kept.length === 0}
            onClick={() => setKeptIds([])}>
            <Message message={messages.removeAllSelection} />
          </Button>
        )}
      </header>

      <ul className={element('list')}>
        {kept.map(item => (
          <li key={item.id} className={element('row')}>
            <div className={element('row-text')}>
              <span className={element('row-name')}>{item.name}</span>
              <span className={element('row-category')}>{item.categoryName}</span>
            </div>
            {!isReadOnly && (
              <button
                type="button"
                aria-label={intl.formatMessage(messages.removeItemAriaLabel, { name: item.name })}
                className={element('row-remove')}
                onClick={() => setKeptIds(current => current.filter(id => id !== item.id))}>
                <Message message={messages.removeSelection} />
              </button>
            )}
          </li>
        ))}
      </ul>

      <footer className={element('footer')}>
        {isReadOnly ? (
          <Button styled="tertiary" className={element('cancel')} onClick={handleCancel}>
            <Message message={messages.reviewMenuClose} />
          </Button>
        ) : (
          <>
            <Button styled="tertiary" className={element('cancel')} onClick={handleCancel}>
              <Message message={messages.reviewMenuCancel} />
            </Button>
            <Button styled="primary" className={element('save')} onClick={handleSave}>
              <Message message={messages.reviewMenuSave} />
            </Button>
          </>
        )}
      </footer>
    </div>
  );
};

ReviewMenuItemsModal.modalConfig = { className: 'review-menu-items-modal__shell' };

ReviewMenuItemsModal.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({ categoryName: PropTypes.string, id: PropTypes.string, name: PropTypes.string })
  ),
  menuName: PropTypes.string,
  onSave: PropTypes.func.isRequired,
};

ReviewMenuItemsModal.defaultProps = {
  items: [],
  menuName: '',
};

export default ReviewMenuItemsModal;
